import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Modal,
  Radio,
  Button,
  Input,
  Row,
  Col,
  Typography,
  notification,
} from "antd";
import API from "api/api";
import MobileEmailChange from "./MobileEmailChange";

const { Text } = Typography;
const api = new API();

const EmailPhoneSelectionModal = ({ open, onClose }) => {
  const [showOTP, setShowOTP] = useState(false);
  const [isOTPSentLoading, setIsOtpSentLoading] = useState(false);
  const [otpMobile, setOtpMobile] = useState(new Array(6).fill(""));
  const [otpEmail, setOtpEmail] = useState(new Array(6).fill(""));
  const [contactMethod, setContactMethod] = useState("");
  const [OTPVerified, setOTPVerified] = useState(false);

  const appState = useSelector((state) => state);
  const { userData } = appState?.auth;
  const { mobile, email, countrycode } = userData;

  const onChangeContactMethod = (contactMethod) => {
    setContactMethod(contactMethod);
  };

  const handleSubmit = async () => {
    if (!contactMethod) {
      return notification.error({ message: "Please select a contact method!" });
    }

    setIsOtpSentLoading(true);

    // Construct payload dynamically
    const payload = Object.assign(
      {},
      (contactMethod === "mobile" || contactMethod === "both") && {
        mobile,
        isdCode: countrycode,
      },
      (contactMethod === "email" || contactMethod === "both") && { email }
    );

    try {
      const response = await api.post("ChangeEmailMobileOTP", {
        data: payload,
      });

      if (response?.StatusCode === 200) {
        notification.success({ message: "OTP sent successfully!" });
        setShowOTP(true);
        setOtpMobile(new Array(6).fill(""));
        setOtpEmail(new Array(6).fill(""));
      } else {
        notification.error({
          message: "Failed to send OTP. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error while sending OTP:", error);
    } finally {
      setIsOtpSentLoading(false);
    }
  };

  const handleChangeOTP = (e, index, type) => {
    const value = e.target.value;
    if (!isNaN(Number(value)) && value.length <= 1) {
      if (type === "mobile") {
        let newOtp = [...otpMobile];
        newOtp[index] = value;
        setOtpMobile(newOtp);
        if (value && index < 5)
          document.getElementById(`otp-mobile-${index + 1}`)?.focus();
      } else {
        let newOtp = [...otpEmail];
        newOtp[index] = value;
        setOtpEmail(newOtp);
        if (value && index < 5)
          document.getElementById(`otp-email-${index + 1}`)?.focus();
      }
    }
  };

  const handleVerifyOTP = async () => {
    const mobileOTP = otpMobile.join("");
    const emailOTP = otpEmail.join("");

    if (contactMethod === "both") {
      if (mobileOTP.length !== 6 || emailOTP.length !== 6) {
        return notification.error({
          message: "Please enter valid 6-digit OTPs for both.",
        });
      }
    } else {
      const enteredOTP = contactMethod === "mobile" ? mobileOTP : emailOTP;
      if (enteredOTP.length !== 6) {
        return notification.error({
          message: "Please enter a valid 6-digit OTP.",
        });
      }
    }

    const payload = Object.assign(
      {},
      (contactMethod === "mobile" || contactMethod === "both") && {
        mobile: userData.mobile,
        isdCode: userData.countrycode,
        mobileCode: mobileOTP,
      },
      (contactMethod === "email" || contactMethod === "both") && {
        email: userData.email,
        emailCode: emailOTP,
      }
    );

    try {
      const response = await api.post("ValidateEmailAndMobileOtp", {
        data: payload,
      });

      if (response?.StatusCode === 200) {
        notification.success({ message: "OTP Verified Successfully!" });
        setOTPVerified(true);
      } else {
        notification.error({
          message: response?.Message || "OTP verification failed.",
        });
      }
    } catch (error) {
      console.error("Error while verifying OTP:", error);
      notification.error({ message: "An error occurred while verifying OTP." });
    }
  };

  let modalTitle = showOTP ? "Verify OTP" : "Select Option";
  if (OTPVerified) {
    modalTitle = "Update Details";
  }

  const handleClose = () => {
    setOTPVerified(false);
    setShowOTP(false);
    onClose();
  };

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      className="email-phone-confirmation-modal"
    >
      <>
        {!showOTP ? (
          <>
            <Radio.Group
              value={contactMethod}
              onChange={(e) => onChangeContactMethod(e.target.value)}
            >
              <Radio value="mobile">Mobile</Radio>
              <Radio value="email">Email</Radio>
              <Radio value="both">Both</Radio>
            </Radio.Group>
            <div
              className="button-container"
              style={{ marginTop: "15px", textAlign: "right" }}
            >
              <Button onClick={handleClose} style={{ marginRight: "8px" }}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={isOTPSentLoading}
              >
                Submit
              </Button>
            </div>
          </>
        ) : (
          <>
            {!OTPVerified && (
              <>
                <p>Enter the 6-digit OTP sent to your {contactMethod}</p>
                {contactMethod === "both" && (
                  <>
                    <Text strong>Mobile OTP</Text>
                    <Row justify="center" gutter={10}>
                      {otpMobile.map((data, index) => (
                        <Col key={index}>
                          <Input
                            id={`otp-mobile-${index}`}
                            value={data}
                            onChange={(e) =>
                              handleChangeOTP(e, index, "mobile")
                            }
                            maxLength={1}
                            style={{ textAlign: "center", width: "40px" }}
                          />
                        </Col>
                      ))}
                    </Row>

                    <Text strong>Email OTP</Text>
                    <Row justify="center" gutter={10}>
                      {otpEmail.map((data, index) => (
                        <Col key={index}>
                          <Input
                            id={`otp-email-${index}`}
                            value={data}
                            onChange={(e) => handleChangeOTP(e, index, "email")}
                            maxLength={1}
                            style={{ textAlign: "center", width: "40px" }}
                          />
                        </Col>
                      ))}
                    </Row>
                  </>
                )}

                {contactMethod !== "both" && (
                  <Row justify="center" gutter={10}>
                    {(contactMethod === "mobile" ? otpMobile : otpEmail).map(
                      (data, index) => (
                        <Col key={index}>
                          <Input
                            id={`otp-${contactMethod}-${index}`}
                            value={data}
                            onChange={(e) =>
                              handleChangeOTP(e, index, contactMethod)
                            }
                            maxLength={1}
                            style={{ textAlign: "center", width: "40px" }}
                          />
                        </Col>
                      )
                    )}
                  </Row>
                )}

                <div
                  className="button-container"
                  style={{ marginTop: "15px", textAlign: "center" }}
                >
                  <Button
                    type="primary"
                    onClick={handleVerifyOTP}
                    style={{ marginRight: "8px" }}
                  >
                    Verify
                  </Button>
                  <Button onClick={handleSubmit}>Resend OTP</Button>
                </div>
              </>
            )}
          </>
        )}

        {OTPVerified && (
          <MobileEmailChange
            contactMethod={contactMethod}
            handleClose={handleClose}
            userData={userData}
          />
        )}
      </>
    </Modal>
  );
};

export default EmailPhoneSelectionModal;
